import axios from 'axios';
import { loginPathFor } from '@/lib/safeNext';

// Same-origin by default: the SPA's nginx proxies /api/* to platform-api (plan P4.2). A non-empty
// REACT_APP_BACKEND_URL still points it elsewhere (e.g. local dev against another API). Without the
// fallback an unset variable made the refresh URL the literal "undefined/api/auth/refresh".
const BASE = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'access_token';
// Patch v17.7 — dedicated storage slot for god-admin impersonation tokens.
// Kept in ``sessionStorage`` (tab-scoped) so opening an impersonation tab
// does NOT disturb the god admin's main session in ``localStorage``.
const IMPERSONATION_TOKEN_KEY = 'impersonation_token';

export const api = axios.create({
  baseURL: BASE,
  timeout: 30000,
  withCredentials: true, // refresh cookie travels with /api/auth/refresh
});

// ---------------------------------------------------------------------------
// Effective-token resolution.
//
// If this tab's ``sessionStorage`` contains an impersonation token, we're
// running inside an impersonation tab and must use THAT token for every
// API call. Otherwise we fall through to the normal ``localStorage``
// access token (the god admin's own session, or a regular member login).
// ---------------------------------------------------------------------------
const _getImpersonationToken = () => {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(IMPERSONATION_TOKEN_KEY) : null;
  } catch {
    return null;
  }
};

const _getEffectiveToken = () => _getImpersonationToken() || localStorage.getItem(TOKEN_KEY);

const _isImpersonating = () => !!_getImpersonationToken();

// Attach the JWT to every request.
api.interceptors.request.use((config) => {
  const token = _getEffectiveToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Step-6A.2 #4 — Silent token-refresh interceptor.
//
// When any /api/* call comes back with HTTP 401, we attempt a single
// /api/auth/refresh round-trip (which presents the HttpOnly refresh cookie).
// If that succeeds, the original request is retried transparently with the
// fresh access token. If refresh fails (or refresh itself 401s) we clear
// the session and bounce the user to /login.
//
// Patch v17.7 caveat — impersonation sessions do NOT auto-refresh. When
// the 60-min impersonation token expires we clear the sessionStorage slot
// and redirect back to the admin console (the parent tab is untouched).
// This prevents the refresh cookie (which belongs to the god admin, not
// the impersonated user) from silently promoting the impersonation tab
// back to god-admin privileges.
// ---------------------------------------------------------------------------
let _refreshInFlight = null;

// Refresh-rejected latch (plan P4.2). Once /api/auth/refresh has answered 401 (the refresh cookie is
// gone, expired or revoked), later 401s go straight to the login redirect instead of each asking
// for a refresh that cannot succeed — a dashboard polling several endpoints would otherwise hit
// the rate-limited auth tier on every tick. A new session (setAccessToken with a token) clears it.
let _refreshRejected = false;

const _isRefreshUrl = (url) => (url || '').includes('/api/auth/refresh');

// A refresh that never got a verdict: the API was unreachable, timed out, or
// answered 5xx — what a platform-api pod being replaced during a deploy looks
// like from here. The refresh cookie is still good, so this must never clear
// the session. Retried a few times over a few seconds before giving up, and
// then surfaced as an outage (REFRESH_UNAVAILABLE), not a sign-out.
const REFRESH_UNAVAILABLE = 'refresh_unavailable';
const REFRESH_RETRY_DELAYS_MS = [0, 1000, 2000, 3000];
const _isUnavailable = (err) => !err?.response || err.response.status >= 500;
const _sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
export const isRefreshUnavailable = (err) => err?.message === REFRESH_UNAVAILABLE;

async function _refreshOnce() {
  const resp = await axios.post(`${BASE}/api/auth/refresh`, {}, { withCredentials: true, timeout: 15000 });
  const fresh = resp?.data?.access_token;
  if (!fresh) throw new Error('no_access_token_in_refresh_response');
  return fresh;
}

async function _doRefresh() {
  if (_refreshRejected) throw new Error('refresh_rejected');
  // De-duplicate concurrent refresh attempts so a burst of 401s only
  // triggers ONE call to /api/auth/refresh.
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    try {
      let lastErr = null;
      for (const delayMs of REFRESH_RETRY_DELAYS_MS) {
        if (delayMs > 0) await _sleep(delayMs);
        try {
          const fresh = await _refreshOnce();
          localStorage.setItem(TOKEN_KEY, fresh);
          return fresh;
        } catch (err) {
          // The API answered and refused (4xx): a verdict. It rotates the
          // refresh token on success, so replaying a refused one gains nothing.
          if (!_isUnavailable(err)) {
            if (err?.response?.status === 401) _refreshRejected = true;
            throw err;
          }
          lastErr = err;
        }
      }
      const unavailable = new Error(REFRESH_UNAVAILABLE);
      unavailable.cause = lastErr;
      throw unavailable;
    } finally {
      _refreshInFlight = null;
    }
  })();
  return _refreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const config = err?.config || {};
    const url = config.url || '';
    // A refused refresh through this instance (AuthContext's boot probe) latches too.
    if (status === 401 && _isRefreshUrl(url)) _refreshRejected = true;
    const isAuthEndpoint = url.includes('/api/auth/login') || url.includes('/api/auth/signup') || url.includes('/api/auth/refresh');
    const isPublic = url.includes('/api/public/') || url.includes('/api/uam/');

    // Patch v17.7 — impersonation sessions do NOT auto-refresh. Clear
    // the tab-scoped token and bounce back to /admin (the god admin's
    // own session in the parent tab / localStorage is untouched).
    if (status === 401 && _isImpersonating() && !isAuthEndpoint && !isPublic) {
      try {
        sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
      } catch {}
      if (typeof window !== 'undefined') {
        window.location.replace('/admin');
      }
      return Promise.reject(err);
    }

    if (status === 401 && !isAuthEndpoint && !isPublic && !config._retried) {
      // Try ONE silent refresh, then replay the original request.
      try {
        const newToken = await _doRefresh();
        config._retried = true;
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newToken}`;
        return api.request(config);
      } catch (refreshErr) {
        // The API never ruled on the session. Keep the token and cookie, and
        // hand the caller the original failure to show as an error; sending the
        // member to /login here is what signed everyone out during API deploys.
        if (isRefreshUnavailable(refreshErr)) {
          return Promise.reject(err);
        }
        localStorage.removeItem(TOKEN_KEY);
        if (typeof window !== 'undefined' && !/\/(login|signup)(\b|\?|\/)/.test(window.location.pathname)) {
          window.location.replace(loginPathFor(window.location));
        }
        return Promise.reject(err);
      }
    }

    if (status === 401 && !isAuthEndpoint && !isPublic) {
      // Only 401 (session truly invalid) terminates the session. 403
      // means the caller is authenticated but lacks permission for the
      // SPECIFIC endpoint — that's a per-route condition that components
      // surface in their own UI (toast, "Access restricted" placeholder,
      // etc.). A 403 must NEVER log a sub-admin out, since sub-admins
      // are intentionally 403'd by god-only endpoints like /api/admin/profit.
      localStorage.removeItem(TOKEN_KEY);
      if (typeof window !== 'undefined' && !/\/(login|signup)(\b|\?|\/)/.test(window.location.pathname)) {
        window.location.replace(loginPathFor(window.location));
      }
    }
    return Promise.reject(err);
  },
);

export const getAccessToken = () => _getEffectiveToken();

export const setAccessToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    _refreshRejected = false;
  } else localStorage.removeItem(TOKEN_KEY);
};

// ---------------------------------------------------------------------------
// Idempotency keys for money actions (plan P4.2; M-5 / M-6). Each call to
// idempotencyHeaders() is ONE user action. The key rides the request config,
// so the interceptor's post-refresh replay resends the SAME key and the API
// recognises the retry instead of buying or withdrawing twice.
// ---------------------------------------------------------------------------
const _newIdempotencyKey = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {}
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
};

export const idempotencyHeaders = () => ({ 'Idempotency-Key': _newIdempotencyKey() });

export const logout = () => setAccessToken(null);

export const getCurrentUserId = () => (getAccessToken() ? 'authenticated' : null);

// ---------------------------------------------------------------------------
// Patch v17.7 — Impersonation helpers (sessionStorage / tab-scoped)
// ---------------------------------------------------------------------------
export const IMPERSONATION_KEY = IMPERSONATION_TOKEN_KEY;

export const setImpersonationToken = (token) => {
  try {
    if (token) sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, token);
    else sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
  } catch {}
};

export const getImpersonationToken = () => _getImpersonationToken();

export const isImpersonating = () => _isImpersonating();
